import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useSearchParams } from 'react-router-dom'
import { createExploreRow } from '../../graphql/mutation'
import { getAllTags } from '../../graphql/queries'
import {
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Grid,
  Divider,
  Checkbox
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ExploreIcon from '@mui/icons-material/Explore'
import SaveIcon from '@mui/icons-material/Save'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'

export default function CreateExploreRowPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [exploreRow, setExploreRow] = useState({
    order: 1,
    isHighlighted: false,
    contentId: '',
    contentType: 'PROMPT',
    platform: ['IOS', 'ANDROID'],
    tagId: ''
  })

  // Pre-fill tagId from URL parameter
  useEffect(() => {
    const tagIdFromUrl = searchParams.get('tagId')
    if (tagIdFromUrl) {
      setExploreRow(prev => ({ ...prev, tagId: tagIdFromUrl }))
    }
  }, [searchParams])

  const [progress, setProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')

  const { data: tagsData } = useQuery(getAllTags)

  const handleChange = (field, value) => {
    setExploreRow(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const [createExploreRowMutation] = useMutation(createExploreRow)

  const handleCreateExploreRow = async () => {
    if (!exploreRow.contentId.trim()) {
      setSnackBarMessage('Please enter a content ID')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    if (exploreRow.order < 1) {
      setSnackBarMessage('Order must be at least 1')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    setProgress(true)
    try {
      const variables = {
        order: exploreRow.order,
        isHighlighted: exploreRow.isHighlighted,
        contentId: exploreRow.contentId.trim(),
        contentType: exploreRow.contentType,
        platform: exploreRow.platform
      }

      // Only include tagId if it's not empty
      if (exploreRow.tagId) {
        variables.tagId = exploreRow.tagId
      }

      const response = await createExploreRowMutation({
        variables
      })

      if (response.data) {
        setSnackBarMessage('Explore row created successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        // Navigate to the new explore row's detail page
        setTimeout(() => {
          navigate(`/explore-rows/${response.data.createExploreRow.id}`)
        }, 1500)
      }
    } catch (error) {
      console.error('Error creating explore row:', error)
      setSnackBarMessage(`Error creating explore row: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'PROMPT': return 'primary'
      case 'STYLE': return 'secondary'
      case 'SECTION': return 'success'
      default: return 'default'
    }
  }

  const availableTags = tagsData?.getAllTags || []

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 4, 
      maxWidth: '800px', 
      mx: 'auto', 
      px: 2, 
      py: 2 
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ExploreIcon fontSize="large" sx={{ color: '#1976d2' }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Create New Explore Row
        </Typography>
      </Box>

      {/* Description */}
      <Box>
        <Typography variant="body1" color="text.secondary">
          Create a new explore row that references content (Prompt, Style, or Section) and defines its display order and highlighting in the explore section.
        </Typography>
      </Box>

      {/* Main Content */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Order"
              type="number"
              value={exploreRow.order}
              onChange={(e) => handleChange('order', parseInt(e.target.value) || 1)}
              inputProps={{ min: 1 }}
              helperText="Display order in explore section (lower numbers appear first)"
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={exploreRow.isHighlighted}
                    onChange={(e) => handleChange('isHighlighted', e.target.checked)}
                  />
                }
                label="Highlighted"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Mark this row as highlighted for special display
              </Typography>
              {exploreRow.isHighlighted && exploreRow.tagId && (
                <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                  <Typography variant="body2">
                    Only one content item per tag can be highlighted. Creating this as highlighted will automatically remove highlighting from other items in the same tag.
                  </Typography>
                </Alert>
              )}
            </Box>
          </Grid>

          {/* Content Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Content Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={exploreRow.contentType}
                label="Content Type"
                onChange={(e) => handleChange('contentType', e.target.value)}
              >
                <MenuItem value="PROMPT">Prompt</MenuItem>
                <MenuItem value="STYLE">Style</MenuItem>
                <MenuItem value="SECTION">Section</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Type of content this row will reference
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
              <Typography variant="body2" color="text.secondary">Selected Type:</Typography>
              <Chip 
                label={exploreRow.contentType} 
                color={getContentTypeColor(exploreRow.contentType)}
                size="small"
                variant="outlined"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Content ID"
              value={exploreRow.contentId}
              onChange={(e) => handleChange('contentId', e.target.value)}
              helperText="ID of the referenced content (must exist in the database)"
              required
              placeholder="Enter the ID of the Prompt, Style, or Section"
            />
          </Grid>

          {/* Platform Selection */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Platform Selection
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exploreRow.platform?.includes('IOS')}
                    onChange={(e) => {
                      const newPlatform = e.target.checked
                        ? [...(exploreRow.platform || []), 'IOS']
                        : (exploreRow.platform || []).filter(p => p !== 'IOS')
                      handleChange('platform', newPlatform)
                    }}
                    icon={<AppleIcon />}
                    checkedIcon={<AppleIcon />}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AppleIcon />
                    <Typography>iOS</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exploreRow.platform?.includes('ANDROID')}
                    onChange={(e) => {
                      const newPlatform = e.target.checked
                        ? [...(exploreRow.platform || []), 'ANDROID']
                        : (exploreRow.platform || []).filter(p => p !== 'ANDROID')
                      handleChange('platform', newPlatform)
                    }}
                    icon={<AndroidIcon />}
                    checkedIcon={<AndroidIcon />}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AndroidIcon />
                    <Typography>Android</Typography>
                  </Box>
                }
              />
            </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> If you select multiple platforms, separate explore rows will be created for each platform (one for iOS, one for Android). This allows you to manage them independently.
                </Typography>
              </Alert>
          </Grid>

          {/* Tag Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Tag Information (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Tag</InputLabel>
              <Select
                value={exploreRow.tagId}
                label="Tag"
                onChange={(e) => handleChange('tagId', e.target.value)}
              >
                <MenuItem value="">No Tag</MenuItem>
                {availableTags.map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Optional tag to associate with this explore row for filtering. If highlighted, only one item per tag can be highlighted at a time.
            </Typography>
          </Grid>

          {/* Preview */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Preview
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5',
                border: '1px dashed #ccc'
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Explore Row Preview:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  <strong>Order:</strong> #{exploreRow.order}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> 
                  <Chip 
                    label={exploreRow.contentType} 
                    color={getContentTypeColor(exploreRow.contentType)}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography variant="body2">
                  <strong>Content ID:</strong> {exploreRow.contentId || 'Not set'}
                </Typography>
                <Typography variant="body2">
                  <strong>Highlighted:</strong> {exploreRow.isHighlighted ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <strong>Platforms:</strong>
                  {exploreRow.platform?.includes('IOS') && (
                    <Chip
                      icon={<AppleIcon />}
                      label="iOS"
                      size="small"
                      sx={{
                        backgroundColor: '#000',
                        color: '#fff',
                        '& .MuiChip-icon': { color: '#fff' }
                      }}
                    />
                  )}
                  {exploreRow.platform?.includes('ANDROID') && (
                    <Chip
                      icon={<AndroidIcon />}
                      label="Android"
                      size="small"
                      sx={{
                        backgroundColor: '#3DDC84',
                        color: '#000',
                        '& .MuiChip-icon': { color: '#000' }
                      }}
                    />
                  )}
                  {!exploreRow.platform?.length && 'None selected'}
                </Typography>
                {exploreRow.tagId && (
                  <Typography variant="body2">
                    <strong>Tag:</strong> {availableTags.find(tag => tag.id === exploreRow.tagId)?.name || exploreRow.tagId}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mt: 4, 
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/explore-rows')}
            disabled={progress}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            startIcon={progress ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleCreateExploreRow}
            disabled={progress}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            {progress ? 'Creating...' : 'Create Explore Row'}
          </Button>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={6000}
        onClose={handleSnackBarClose}
      >
        <Alert onClose={handleSnackBarClose} severity={snackBarSeverity}>
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
